import { Container, Typography, Button, Box, Grid, Paper, Card, CardContent } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import EngineeringIcon from '@mui/icons-material/Engineering';
import BuildIcon from '@mui/icons-material/Build';
import ArchitectureIcon from '@mui/icons-material/Architecture';

const Home = () => {
  const features = [
    {
      icon: <ArchitectureIcon sx={{ fontSize: 40 }} />,
      title: 'Διαχείριση Έργων',
      description:
        'Ολοκληρωμένες υπηρεσίες διαχείρισης έργων από τον σχεδιασμό έως την εκτέλεση.',
    },
    {
      icon: <BuildIcon sx={{ fontSize: 40 }} />,
      title: 'Επίβλεψη Έργων',
      description:
        'Επαγγελματική επίβλεψη και έλεγχος ποιότητας για κατασκευαστικά έργα.',
    },
  ];

  return (
    <Box>
      {/* Hero Section with Background Image */}
      <Box
        sx={{
          position: 'relative',
          height: '80vh',
          backgroundImage: 'url("/images/civil-bg.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'center',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.4), rgba(0,0,0,0.8))',
          },
        }}
        role="img"
        aria-label="Κρουσταλλάς Αθανάσιος - Πολιτικός Μηχανικός ΑΠΘ στη Βόρεια Ελλάδα, Σέρρες, Τούζλα και Κερδύλλια"
      >
        <Container maxWidth="lg">
          <Box sx={{ position: 'relative', color: 'white', textAlign: 'center' }}>
            <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 800, fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif', letterSpacing: '-0.02em' }}>
              Κρουσταλλάς Αθανάσιος - Πολιτικός Μηχανικός ΑΠΘ
            </Typography>
            <Typography variant="h5" gutterBottom sx={{ mb: 4, fontWeight: 300, color: 'rgba(255,255,255,0.9)' }}>
              Επαγγελματικές υπηρεσίες σε Σέρρες, Τούζλα, Κερδύλλια και όλη τη Βόρεια Ελλάδα
            </Typography>
            <Button
              variant="contained"
              size="large"
              component={RouterLink}
              to="/contact"
              sx={{
                bgcolor: 'primary.main',
                transition: 'all 0.3s ease',
                '&:hover': {
                  bgcolor: 'primary.main',
                  transform: 'scale(1.05)',
                  filter: 'brightness(0.9)',
                },
              }}
            >
              Ξεκινήστε
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Services Preview Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" align="center" gutterBottom>
          Οι Υπηρεσίες μας
        </Typography>
        <Typography
          variant="h6"
          align="center"
          color="text.secondary"
          paragraph
          sx={{ mb: 6 }}
        >
          Ολοκληρωμένες λύσεις για τις ανάγκες σας
        </Typography>

        <Grid container spacing={4} justifyContent="center">
          <Grid item xs={12} sm={6} md={4}>
            <Card
              elevation={1}
              sx={{
                height: '100%',
                mx: 'auto',
                maxWidth: '350px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: 6,
                }
              }}
            >
              <CardContent sx={{ textAlign: 'center' }}>
                <EngineeringIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h5" component="h3" gutterBottom>
                  Δομοστατική Μηχανική
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Σχεδιασμός και ανάλυση δομών για κτίρια και υποδομές
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card
              elevation={1}
              sx={{
                height: '100%',
                mx: 'auto',
                maxWidth: '350px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: 6,
                }
              }}
            >
              <CardContent sx={{ textAlign: 'center' }}>
                <BuildIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h5" component="h3" gutterBottom>
                  Κατασκευές
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Διαχείριση και επίβλεψη κατασκευαστικών έργων
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card
              elevation={1}
              sx={{
                height: '100%',
                mx: 'auto',
                maxWidth: '350px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: 6,
                }
              }}
            >
              <CardContent sx={{ textAlign: 'center' }}>
                <ArchitectureIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h5" component="h3" gutterBottom>
                  Σχεδιασμός
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Σχεδιασμός υποδομών και πολεοδομικός σχεδιασμός
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={4} justifyContent="center">
          {features.map((feature, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Paper
                elevation={3}
                sx={{
                  p: 4,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  maxWidth: '400px',
                  mx: 'auto'
                }}
              >
                <Box sx={{ color: 'primary.main', mb: 2 }}>{feature.icon}</Box>
                <Typography variant="h5" component="h3" gutterBottom>
                  {feature.title}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {feature.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box sx={{ bgcolor: 'grey.50', py: 6, mt: 6 }}>
        <Container maxWidth="md">
          <Typography variant="h4" component="h2" align="center" gutterBottom>
            Έτοιμοι να Ξεκινήσετε το Έργο σας;
          </Typography>
          <Typography
            variant="body1"
            align="center"
            color="text.secondary"
            paragraph
            sx={{ mb: 4 }}
          >
            Επικοινωνήστε μαζί μας σήμερα για μια διαβούλευση και ας κάνουμε πραγματικότητα το όραμά σας.
          </Typography>
          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              component={RouterLink}
              to="/contact"
            >
              Επικοινωνία
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Home; 